import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { CTASection } from "@/components/CTASection";
import { Reveal, EASE } from "@/components/Reveal";
import { FACILITY_ITEMS, FACILITY_FEATURES, IMG } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";

export default function Facilities() {
  useSeo({
    title: "Facilities | DST New Braunfels — State-of-the-Art Training Facility",
    description:
      "Tour the brand-new DST New Braunfels facility: indoor batting cages, HitTrax technology, an elite strength & conditioning floor, turf training bays, and climate-controlled space in New Braunfels, TX.",
  });

  return (
    <>
      <PageHeader
        eyebrow="Our Facility"
        title={<>Brand-New.<br />Elite-Level.</>}
        subtitle="Step inside one of the most complete training facilities in New Braunfels — purpose-built, fully modern, and dialed in for developing competitors."
        image={IMG.facilityGym}
      />

      {/* Intro */}
      <section className="bg-dst-ink py-20 lg:py-28 grain">
        <div className="container-dst relative z-10 grid lg:grid-cols-[1fr_1fr] gap-14 items-center">
          <Reveal>
            <span className="eyebrow text-dst-red">Newly Built & Up To Date</span>
            <h2 className="mt-4 font-oswald font-700 uppercase tracking-tighter leading-[0.9] text-4xl lg:text-6xl">
              A Facility Built<br />For Mastery
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed text-lg">
              Everything at DST New Braunfels is brand-new and up to date — from our indoor batting cages and HitTrax
              hitting stations to a full strength-and-conditioning floor and turf-lined training bays. No worn-out
              equipment, no crowding, no compromises. Just a clean, modern space engineered to help every athlete
              align their mind and body and train like a pro, year-round.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative">
              <img src={IMG.facilityTunnels} alt="DST indoor batting cages" className="w-full h-[420px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-dst-red p-6 hidden sm:block">
                <div className="font-oswald text-4xl font-700 leading-none">100%</div>
                <div className="text-white/80 text-xs tracking-[0.2em] uppercase mt-1">Indoor · Climate<br />Controlled</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="bg-dst-light text-dst-ink py-20 lg:py-28">
        <div className="container-dst">
          <Reveal className="mb-14 max-w-2xl">
            <span className="eyebrow text-dst-red">What's Inside</span>
            <h2 className="mt-4 font-oswald font-700 uppercase tracking-tighter leading-[0.9] text-4xl lg:text-6xl text-dst-navy">
              Everything You Need<br />Under One Roof
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FACILITY_FEATURES.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Building2;
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.08}>
                  <div className="bg-white p-8 h-full shadow-[0_20px_50px_-25px_rgba(0,34,68,0.3)] border-t-2 border-dst-red" data-testid={`facility-feature-${i}`}>
                    <Icon size={30} className="text-dst-red mb-5" />
                    <h3 className="font-oswald uppercase text-2xl text-dst-navy mb-3">{f.title}</h3>
                    <p className="text-dst-ink/60 leading-relaxed">{f.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photo showcase */}
      <section className="bg-dst-ink py-20 lg:py-28 grain" data-testid="facility-showcase">
        <div className="container-dst relative z-10">
          <Reveal className="mb-14">
            <span className="eyebrow text-dst-red">Take A Look</span>
            <h2 className="mt-4 font-oswald font-700 uppercase tracking-tighter leading-[0.9] text-4xl lg:text-6xl">
              Inside DST New Braunfels
            </h2>
          </Reveal>

          <div className="space-y-6">
            {FACILITY_ITEMS.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE }}
                data-testid={`facility-item-${i}`}
                className={`grid lg:grid-cols-2 gap-0 items-stretch overflow-hidden border border-white/10 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="group relative overflow-hidden">
                  <img
                    src={IMG[item.img]}
                    alt={item.label}
                    loading="lazy"
                    className="w-full h-72 lg:h-full min-h-[320px] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="bg-dst-surface p-8 lg:p-14 flex flex-col justify-center">
                  <div className="font-oswald text-6xl font-700 text-white/10 leading-none mb-4">
                    0{i + 1}
                  </div>
                  <h3 className="font-oswald uppercase tracking-tight text-3xl lg:text-4xl mb-4">{item.label}</h3>
                  <p className="text-white/60 leading-relaxed text-lg">{item.caption}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
